'use client';

import React, { useEffect, useMemo } from 'react';
import Link from 'next/link';
import { format } from 'date-fns/format';
import { NewsData } from '@wwe-portal/entities/news.type';

import { FluidPageContainer } from '../../common/layout/fluid-page-container';
import { SvgWpDuotone } from './SvgDuotone';
import styles from './blog.module.scss';
import './fork-awesome.min.css';

// This is the click handler used for all interaction within blog page content
//   we will need to narrow down the actual event target, since it's injected into
//   a react component as one big blob of html
//
function generalizedClickHandler(
  ev: React.MouseEvent<HTMLDivElement, MouseEvent>
) {
  const target = ev.target;
  if (target) {
    const elSpoilerContent = (target as any)?.closest('.su-spoiler-content');
    if (!elSpoilerContent) {
      const elSpoiler = (ev.target as any)?.closest('.su-spoiler');
      if (elSpoiler) {
        elSpoiler.classList.toggle('su-spoiler-closed');
      }
    }
  }
}

type Props = {
  post: NewsData;
};

export const BlogPostContent = ({ post }: Props) => {
  const content = useMemo(() => post.content || '', [post.content]);
  const dateString = useMemo(() => {
    if (!post.pubDate) return '';
    return format(new Date(post.pubDate), 'MMMM d, y');
  }, [post.pubDate]);

  // Make sure we're at the top of the page when we navigate here
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.scrollTo({
      top: 0,
    });
  }, [content]);

  return (
    <FluidPageContainer>
      <div className={styles.body}>
        {content.includes('wp-duotone-magenta-yellow') ? (
          <SvgWpDuotone id="magenta-yellow" />
        ) : null}
        {content.includes('wp-duotone-purple-green') ? (
          <SvgWpDuotone id="purple-green" />
        ) : null}
        {post.category === 'News' ? (
          <>
            {post.image ? (
              <div className={styles.thumbnail}>
                <img
                  src={post.image.src}
                  className="attachment-large size-large wp-post-image"
                  alt=""
                  decoding="async"
                  srcSet={post.image.srcSet}
                  sizes={post.image.sizes}
                />
              </div>
            ) : null}
            <div className="post-meta-wrap">
              <div className="post-meta">
                post created by <strong>Scopely</strong> on{' '}
                <strong>{dateString}</strong>
              </div>
              <div className="post-category accent-tipo">
                <Link href="/news" title="news">
                  News
                </Link>
              </div>
            </div>
          </>
        ) : null}
        {post.titleTwemoji ? (
          <h1
            className={styles.title}
            dangerouslySetInnerHTML={{ __html: post.titleTwemoji }}
          />
        ) : (
          <h1 className={styles.title}>{post.title}</h1>
        )}
        {content ? (
          <div
            className={styles.blog}
            onClick={generalizedClickHandler}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : null}
      </div>
      <div className={styles.footer} />
    </FluidPageContainer>
  );
};
